/**
 * AdminErrorBoundary — wrap each section/room in this so a single
 * crash doesn't take down the whole admin shell. Renders a small,
 * recoverable error card with a "Reload section" button.
 *
 * Logged to console + Sentry (when available) so admins see the error
 * AND we get the trace for debugging.
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

const GOLD = "#E2DD9F";
const ff = "'DM Sans',sans-serif";
const hf = "'Bebas Neue',sans-serif";

export class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error(`[AdminErrorBoundary:${this.props.label || "unknown"}]`, error, info);
    // Best-effort Sentry capture
    if (typeof window !== "undefined" && window.Sentry?.captureException) {
      try {
        window.Sentry.captureException(error, {
          tags: { surface: "admin", scope: this.props.label || "unknown" },
          extra: info,
        });
      } catch {}
    }
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error);
    const stack = this.state.error?.stack || "";

    return (
      <div style={{
        padding:"32px 28px",maxWidth:640,margin:"40px auto",fontFamily:ff,
      }}>
        <div style={{
          background:"rgba(255,107,107,0.06)",
          border:"1px solid rgba(255,107,107,0.35)",
          borderRadius:14,
          padding:"22px 24px",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <AlertTriangle size={20} color="#FF6B6B"/>
            <div style={{fontFamily:hf,fontSize:18,letterSpacing:1.6,color:"#FF6B6B"}}>
              SOMETHING BROKE
            </div>
          </div>
          <div style={{fontSize:13,color:"#C9C3B3",lineHeight:1.55,marginBottom:14}}>
            The <strong style={{color:"#F5F0E8"}}>{this.props.label || "page"}</strong> tab errored
            while rendering. The rest of the admin is fine — just this section is down.
            Reload to try again. If it keeps breaking, screenshot this and send it over.
          </div>
          <div style={{
            background:"#0A0A0A",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8,
            padding:"10px 12px",marginBottom:14,
            fontFamily:"'JetBrains Mono',monospace",fontSize:11,color:"#FF6B6B",
            whiteSpace:"pre-wrap",wordBreak:"break-word",
            maxHeight:160,overflowY:"auto",
          }}>
            {msg}
            {stack && (
              <div style={{color:"#9A9488",fontSize:10,marginTop:8,opacity:.7}}>
                {stack.split("\n").slice(0, 5).join("\n")}
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={this.reset}
              style={{
                display:"inline-flex",alignItems:"center",gap:6,
                background: GOLD, color:"#0A0A0A", border:"none",
                padding:"10px 16px",borderRadius:8,fontSize:12,fontWeight:800,
                cursor:"pointer",fontFamily:ff,letterSpacing:.3,
              }}>
              <RefreshCw size={13}/> Reload section
            </button>
            <button onClick={() => { window.location.hash = "#/admin/home"; this.reset(); }}
              style={{
                background:"transparent",
                border:"1px solid rgba(255,255,255,0.12)",
                color:"#C9C3B3",
                padding:"10px 16px",borderRadius:8,fontSize:12,fontWeight:700,
                cursor:"pointer",fontFamily:ff,letterSpacing:.3,
              }}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
